import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';
import { decodeToken } from './authUtils.js';

const initialState = {
  user: null,
  token: null,
  loading: true,
};

export const initializeAuth = createAsyncThunk('auth/initialize', async () => {
  const token = localStorage.getItem('token');

  if (!token) {
    delete api.defaults.headers.common['Authorization'];
    return { user: null, token: null };
  }

  const user = decodeToken(token);
  if (!user) {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    delete api.defaults.headers.common['Authorization'];
    return { user: null, token: null };
  }

  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  return { user, token };
});

export const login = createAsyncThunk(
  'auth/login',
  async ({ identifier, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { identifier, password });
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      const user = decodeToken(access_token);
      if (!user) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return rejectWithValue({
          status: 401,
          message: 'Invalid token received.',
        });
      }

      return { user, token: access_token };
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Login failed. Please try again.';

      return rejectWithValue({
        status,
        message: typeof message === 'string' ? message : 'Login failed. Please try again.',
        data: error?.response?.data,
      });
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (_, { dispatch, rejectWithValue }) => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      dispatch(loggedOut());
      return rejectWithValue({ message: 'No refresh token available.' });
    }

    try {
      const response = await api.post('/auth/refresh', { refreshToken });
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      const user = decodeToken(access_token);
      if (!user) {
        dispatch(loggedOut());
        return rejectWithValue({ message: 'Invalid token received.' });
      }

      return { user, token: access_token };
    } catch (error) {
      dispatch(loggedOut());
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to refresh token.';

      return rejectWithValue({
        status,
        message: typeof message === 'string' ? message : 'Failed to refresh token.',
        data: error?.response?.data,
      });
    }
  }
);

export const registerUser = createAsyncThunk('auth/register', async (userData) => {
  try {
    await api.post('/auth/register', userData);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || 'Registration failed',
    };
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loggedOut(state) {
      state.user = null;
      state.token = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.loading = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.loading = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state) => {
        state.user = null;
        state.token = null;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
      });
  },
});

export const logout = () => (dispatch) => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  delete api.defaults.headers.common['Authorization'];
  dispatch(loggedOut());
};

export const { loggedOut } = authSlice.actions;

export const selectAuthUser = (state) => state.auth.user;
export const selectAuthToken = (state) => state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectIsAuthenticated = (state) => Boolean(state.auth.token);

export default authSlice.reducer;
